import { useState, useRef, useCallback, useEffect } from 'react';
import { VideoElement } from '../../types/player';

export const useEndScreen = (
  videoElement: VideoElement,
  setIsPlaying: (isPlaying: boolean) => void,
  handleSeek: (time: number) => void,
  saveLessonProgress: (progress: {
    current_second: number;
    progress_status: 'completed' | 'in_progress';
  }) => void,
  handleNextVideo: () => void,
) => {
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const nextVideoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear countdown - moved before handleNextVideo to avoid circular dependency
  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (nextVideoTimeoutRef.current) {
      clearTimeout(nextVideoTimeoutRef.current);
      nextVideoTimeoutRef.current = null;
    }

    setCountdown(null);
  }, []);

  const startCountdown = useCallback(
    (seconds: number) => {
      setCountdown(seconds);

      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }

      // Clear any existing next video timeout
      if (nextVideoTimeoutRef.current) {
        clearTimeout(nextVideoTimeoutRef.current);
        nextVideoTimeoutRef.current = null;
      }

      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null) return null;

          const newCount = prev - 1;

          if (newCount <= 0) {
            if (countdownRef.current) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }

            // Schedule handleNextVideo to be called after a short delay
            nextVideoTimeoutRef.current = setTimeout(() => {
              handleNextVideo();
            }, 500);

            return 0;
          }

          return newCount;
        });
      }, 1000);
    },
    [handleNextVideo],
  );

  // Handle video ended
  const handleEnded = useCallback(() => {
    setShowEndScreen(true);
    setIsPlaying(false);
    startCountdown(10);

    // Mark as completed when video ends
    if (videoElement && saveLessonProgress) {
      saveLessonProgress({
        current_second: Math.round(videoElement.duration),
        progress_status: 'completed',
      });
    }
  }, [videoElement, setIsPlaying, startCountdown, saveLessonProgress]);

  // Handle restart video
  const handleRestart = useCallback(() => {
    clearCountdown();
    handleSeek(0);
    setIsPlaying(true);
    setShowEndScreen(false);

    if (videoElement) {
      videoElement.play().catch((err) => {});
    }
  }, [clearCountdown, handleSeek, setIsPlaying, videoElement]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (nextVideoTimeoutRef.current) {
        clearTimeout(nextVideoTimeoutRef.current);
      }
    };
  }, []);

  return {
    showEndScreen,
    setShowEndScreen,
    countdown,
    handleEnded,
    handleNextVideo,
    handleRestart,
    startCountdown,
    clearCountdown,
  };
};
